import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_BASE, getCurrentToken, getTenantSlug } from '../api/client';
import { useEvents } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { RsvpImportPanel } from '../components/rsvp-import/RsvpImportPanel';
import { RsvpImportHistory } from '../components/rsvp-import/RsvpImportHistory';
import { EventSetupWizard } from '../components/events/EventSetupWizard';
import { isAtLeast } from '../utils/roles';
import { IntentLauncher, IntentAction } from '../components/overview/IntentLauncher';
import { StatCard } from '../components/overview/StatCard';
import { AttentionStrip, AttentionSignal } from '../components/overview/AttentionStrip';
import { Reveal } from '../components/overview/Reveal';
import { EventReadinessAdvisor } from '../components/overview/EventReadinessAdvisor';
import { EventBriefing } from '../components/overview/EventBriefing';
import { EventJourneySummary } from '../components/overview/EventJourneySummary';
import { useParallax } from '../hooks/useParallax';
import { isCustomerRole } from '../config/navConfig';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { OperatorMobileHomePage } from './OperatorMobileHomePage';

/**
 * The spinoff dashboard has two modes:
 *  - Portfolio (no event in scope): a tenant-wide overview of all events with
 *    aggregate roll-ups and a card per event. Pick an event to drill in.
 *  - Event (an event in scope): an operations summary for the selected event
 *    with its own scoped metrics and quick actions.
 *
 * Which mode renders is driven entirely by the selected event in EventContext.
 */

interface PortfolioEventCard {
  id: string;
  name: string;
  date: string | null;
  venueName: string | null;
  guestCount: number;
  tableCount: number;
  ticketsSold: number;
  isPast: boolean;
  status: string;
}

interface PortfolioSummary {
  eventCount: number;
  activeCount: number;
  archivedCount: number;
  upcomingCount: number;
  pastCount: number;
  totalGuests: number;
  totalTables: number;
  totalSeats: number;
  totalAssignedSeats: number;
  totalTicketsSold: number;
  totalRevenue: number;
  totalCheckedIn: number;
  ticketedActiveCount: number;
  freeActiveCount: number;
  events: PortfolioEventCard[];
}

interface EventSummary {
  eventId: string;
  eventName: string;
  tenantName: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  venueName: string | null;
  maxCapacity: number;
  guestCount: number;
  zoneCount: number;
  tableCount: number;
  seatCount: number;
  assignedSeats: number;
  checkedIn: number;
  ticketsSold: number;
  ticketRevenue: number;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Date TBD';
  const datePart = iso.includes('T') ? iso.split('T')[0] : iso;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return 'Date TBD';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function DashboardPage() {
  const { selectedEventId } = useEvents();
  const { user } = useAuth();
  const isMobile = useMediaQuery(1023);

  // Pillar-aware operator mobile home (Phase 1): customer-tier roles on a phone
  // get the condensed, pillar-sectioned landing instead of the desktop
  // portfolio/event dashboards. Desktop and legacy/attendee flows are untouched.
  if (isMobile && isCustomerRole(user?.role || '')) {
    return <OperatorMobileHomePage />;
  }

  // The AppLayout keys the routed subtree off selectedEventId, so this component
  // remounts on event switch and the correct mode loads fresh.
  return selectedEventId ? <EventDashboard /> : <PortfolioDashboard />;
}

// ── Portfolio mode (no event selected) ──────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

interface PortfolioReadiness {
  eventId: string;
  eventName: string;
  percent: number;
  completed: number;
  total: number;
  nextStep: { key: string; label: string; detail: string; path: string } | null;
  gaps: { key: string; label: string; detail: string; path: string }[];
}

interface GivingSummary {
  campaignCount: number;
  totalRaised: number;
  combinedGoal: number;
  donorCount: number;
  percentToGoal: number;
}

function PortfolioDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectEvent, events: ctxEvents, refresh } = useEvents();
  const { offset, onMouseMove, onMouseLeave } = useParallax(20);
  const [readiness, setReadiness] = useState<PortfolioReadiness[]>([]);
  // Tenant-level pillar modules (e.g. 'giving') — drives which pillar-specific
  // intents and bento bands appear.
  const [tenantModules, setTenantModules] = useState<string[]>([]);
  // Tenant-wide Giving roll-up (only loaded when the 'giving' pillar is on).
  const [giving, setGiving] = useState<GivingSummary | null>(null);
  // Fresh fullName from /auth/me. The JWT-decoded `user.fullName` can be stale
  // right after a Team-page rename (token not yet rotated); /auth/me is live.
  const [freshName, setFreshName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient<PortfolioReadiness[]>('/dashboard/portfolio-readiness')
      .then((r) => { if (!cancelled) setReadiness(r || []); })
      .catch(() => { if (!cancelled) setReadiness([]); });
    apiClient<{ tenantModules?: string[]; fullName?: string }>('/auth/me')
      .then((r) => {
        if (cancelled) return;
        if (r?.fullName) setFreshName(r.fullName);
        const mods = r?.tenantModules ?? [];
        setTenantModules(mods);
        // Load the Giving roll-up only for giving-enabled tenants.
        if (mods.includes('giving')) {
          apiClient<GivingSummary>('/dashboard/giving-summary')
            .then((g) => { if (!cancelled) setGiving(g); })
            .catch(() => { if (!cancelled) setGiving(null); });
        }
      })
      .catch(() => { if (!cancelled) setTenantModules([]); });
    return () => { cancelled = true; };
  }, []);
  const [data, setData] = useState<PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const canCreate = isAtLeast(user?.role || '', 'Organizer');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await apiClient<PortfolioSummary>('/dashboard/portfolio');
        if (!cancelled) { setData(res); setError(''); }
      } catch {
        if (!cancelled) setError('Could not load your events overview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function reload() {
    try {
      const res = await apiClient<PortfolioSummary>('/dashboard/portfolio');
      setData(res);
    } catch { /* keep prior */ }
  }

  function work(eventId: string) {
    selectEvent(eventId);
    navigate('/dashboard');
  }

  async function changeStatus(eventId: string, status: 'Draft' | 'Live' | 'Archived') {
    try {
      await apiClient(`/events/${eventId}/status`, { method: 'PATCH', body: { status } });
      await Promise.all([reload(), refresh()]);
    } catch (e: any) {
      setError(e?.message || 'Could not change event status.');
    }
  }

  if (loading) return <div style={styles.page}><p style={styles.muted}>Loading your events…</p></div>;

  const active = (data?.events ?? []).filter((e) => e.status !== 'Archived');
  const archived = (data?.events ?? []).filter((e) => e.status === 'Archived');

  const renderCard = (ev: PortfolioEventCard) => (
    <div key={ev.id} style={{ ...styles.eventCard, ...(ev.status === 'Archived' ? styles.eventCardArchived : {}) }}>
      <div style={styles.eventCardTop}>
        <span style={styles.eventName}>{ev.name}</span>
        <StatusPill status={ev.status} isPast={ev.isPast} />
      </div>
      <div style={styles.eventMeta}>📅 {formatDate(ev.date)}</div>
      <div style={styles.eventMeta}>📍 {ev.venueName || 'TBD'}</div>
      <div style={styles.eventStats}>
        <span><strong>{ev.guestCount}</strong> guests</span>
        <span><strong>{ev.tableCount}</strong> tables</span>
        <span><strong>{ev.ticketsSold}</strong> tickets</span>
      </div>
      <div style={styles.cardActions}>
        {ev.status !== 'Archived' ? (
          <>
            <button style={styles.workBtn} onClick={() => work(ev.id)}>Work on this →</button>
            {ev.status === 'Draft' && (
              <button style={styles.linkBtn} onClick={() => changeStatus(ev.id, 'Live')} title="Publish">Publish</button>
            )}
            <button style={styles.linkBtn} onClick={() => changeStatus(ev.id, 'Archived')} title="Archive">Archive</button>
          </>
        ) : (
          <button style={styles.linkBtn} onClick={() => changeStatus(ev.id, 'Live')} title="Restore">↩ Restore</button>
        )}
      </div>
    </div>
  );

  // Prefer the live name from /auth/me (reflects a just-saved rename) over the
  // JWT-decoded name, which lags until the token rotates. Treat the provisioning
  // placeholder "Tenant Admin" as "no real name set" so we don't greet "Tenant".
  const resolvedName = freshName ?? user?.fullName ?? '';
  const isPlaceholderName = resolvedName.trim().toLowerCase() === 'tenant admin';
  const firstName = isPlaceholderName
    ? 'there'
    : resolvedName.split(' ')[0] || 'there';
  const seatedPct = data && data.totalSeats > 0 ? Math.round((data.totalAssignedSeats / data.totalSeats) * 100) : 0;
  const intents: IntentAction[] = [
    {
      key: 'create-event',
      label: 'Create an event',
      description: 'Spin up a new event in your workspace.',
      icon: '➕',
      gradient: ['#1a6b3c', '#15803d'],
      onClick: () => navigate('/events'),
      featured: true,
    },
    {
      key: 'guests',
      label: 'Manage guests',
      description: 'Registrations, imports, and the guest list.',
      icon: '🎟️',
      gradient: ['#0ea5e9', '#2563eb'],
      onClick: () => navigate('/registrations'),
    },
    {
      key: 'seating',
      label: 'Seating & tables',
      description: 'Floorplan, tables, and the seating engine.',
      icon: '🪑',
      gradient: ['#8b5cf6', '#6d28d9'],
      onClick: () => navigate('/manage-event'),
    },
    {
      key: 'venues',
      label: 'Venues',
      description: 'Where your events take place.',
      icon: '📍',
      gradient: ['#f59e0b', '#ea580c'],
      onClick: () => navigate('/venues'),
    },
  ];

  // Giving pillar intent — only for tenants with the 'giving' module enabled.
  // Opens the Campaigns pillar to start a new fundraising campaign.
  if (tenantModules.includes('giving')) {
    intents.push({
      key: 'create-giving-campaign',
      label: 'Create Giving Campaign',
      description: 'Start a year-round fundraising campaign.',
      icon: '💚',
      gradient: ['#1a6b3c', '#3ddc84'],
      onClick: () => navigate('/giving?new=1'),
    });
  }

  const signals: AttentionSignal[] = [];
  const drafts = (data?.events ?? []).filter((e) => e.status === 'Draft');
  const emptyEvents = (data?.events ?? []).filter((e) => e.status !== 'Archived' && e.guestCount === 0);
  if (drafts.length > 0) {
    signals.push({ id: 'drafts', severity: 'warning', icon: '📝', text: `${drafts.length} draft event${drafts.length === 1 ? '' : 's'} to publish`, onClick: () => { selectEvent(drafts[0].id); navigate('/dashboard'); } });
  }
  if (emptyEvents.length > 0) {
    signals.push({ id: 'empty', severity: 'info', icon: '👥', text: `${emptyEvents.length} event${emptyEvents.length === 1 ? '' : 's'} with no guests yet`, onClick: () => { selectEvent(emptyEvents[0].id); navigate('/dashboard'); } });
  }
  if ((data?.eventCount ?? 0) === 0) {
    signals.push({ id: 'no-events', severity: 'info', icon: '📅', text: 'No events yet — create your first', onClick: () => navigate('/events') });
  }

  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 860px) {
          .ep-org-bento { grid-template-columns: 1fr 1fr !important; grid-template-areas: "hero hero" "guests seated" "checked revenue" !important; }
        }
        @media (max-width: 560px) {
          .ep-org-bento { grid-template-columns: 1fr !important; grid-template-areas: "hero" "guests" "seated" "checked" "revenue" !important; }
        }
      `}</style>
      {/* Green hero */}
      <div style={styles.orgHero} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        <span style={{ ...styles.orgHeroGlow, transform: `translate(${offset.x}px, ${offset.y}px)` }} aria-hidden />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={styles.orgKicker}>Your Workspace</div>
            <h1 style={styles.orgHeroTitle}>{greeting()}, {firstName}.</h1>
            <p style={styles.orgHeroSub}>
              {data ? data.activeCount : '…'} active event{data && data.activeCount === 1 ? '' : 's'} ·{' '}
              {data ? data.totalGuests.toLocaleString() : '…'} guests · {data ? data.totalCheckedIn.toLocaleString() : '…'} checked in
            </p>
          </div>
          {canCreate && (
            <button type="button" style={styles.createEventBtn} onClick={() => setWizardOpen(true)}>
              ＋ Create event
            </button>
          )}
        </div>
      </div>

      {error && <div style={styles.error} onClick={() => setError('')}>{error}</div>}

      {wizardOpen && <EventSetupWizard onClose={() => setWizardOpen(false)} />}

      <IntentLauncher actions={intents} />

      <AttentionStrip signals={signals} />

      {/* Events needing attention — point-in-time gap advisor */}
      {readiness.length > 0 && (
        <div style={styles.advisorCard}>
          <div style={styles.advisorHead}>
            <h2 style={styles.advisorTitle}>✨ Events needing attention</h2>
            <span style={styles.advisorCount}>{readiness.length} not ready</span>
          </div>
          <div style={styles.advisorList}>
            {readiness.slice(0, 5).map((r) => (
              <div key={r.eventId} style={styles.advisorRow}>
                <button
                  style={styles.advisorName}
                  onClick={() => { selectEvent(r.eventId); navigate('/dashboard'); }}
                >
                  <span style={{ fontWeight: 800, color: '#0f2d1f' }}>{r.eventName}</span>
                  <span style={{ fontSize: '0.75rem', color: '#5b7366' }}>{r.completed}/{r.total} ready · {r.gaps.length} to go</span>
                </button>
                <div style={styles.advisorBar}>
                  <div style={{ flex: 1, height: 7, background: '#e5efe9', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: `${r.percent}%`, height: '100%', background: r.percent >= 70 ? '#1a6b3c' : '#c9a84c' }} />
                  </div>
                  <span style={{ fontSize: '0.74rem', color: '#5b7366', width: 32, textAlign: 'right' }}>{r.percent}%</span>
                </div>
                {r.nextStep && (
                  <button
                    style={styles.advisorNext}
                    onClick={() => { selectEvent(r.eventId); navigate(r.nextStep!.path); }}
                    title={r.nextStep.detail}
                  >
                    Next: {r.nextStep.label} →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bento roll-up */}
      <div style={styles.orgBento} className="ep-org-bento">
        <Reveal index={0} style={{ gridArea: 'hero', display: 'flex' }}>
          <button type="button" style={{ ...styles.bentoTile, ...styles.orgBentoHero }} onClick={() => navigate('/events')}>
            <span aria-hidden style={styles.orgBentoHeroGlow} />
            <div style={styles.orgBentoTop}>
              <span style={styles.orgBentoChip}>📅 Active events</span>
              <span style={styles.orgBentoCta}>Manage →</span>
            </div>
            <div style={styles.orgBentoValue}>{data?.activeCount ?? 0}</div>
            {(data?.activeCount ?? 0) > 0 && (
              <div style={styles.orgBentoSplit}>
                <span style={styles.orgBentoSplitItem}>🎟️ {data?.ticketedActiveCount ?? 0} ticketed</span>
                <span style={styles.orgBentoSplitDot}>·</span>
                <span style={styles.orgBentoSplitItem}>🎉 {data?.freeActiveCount ?? 0} free</span>
              </div>
            )}
            <div style={styles.orgBentoSub}>{data?.upcomingCount ?? 0} upcoming · {data?.archivedCount ?? 0} archived</div>
          </button>
        </Reveal>
        <Reveal index={1} style={{ gridArea: 'guests', display: 'flex' }}>
          <StatCard label="Guests" value={data?.totalGuests ?? 0} icon="🎟️" accent="#0ea5e9" hint="in active events" drillLabel="Manage" onClick={() => navigate('/registrations')} />
        </Reveal>
        <Reveal index={2} style={{ gridArea: 'seated', display: 'flex' }}>
          <StatCard
            label="Seated"
            value={seatedPct}
            icon="🪑"
            accent="#8b5cf6"
            format={(n) => `${n}%`}
            hint={`${data?.totalAssignedSeats ?? 0} of ${data?.totalSeats ?? 0} seats`}
            drillLabel="Seating"
            onClick={() => navigate('/manage-event')}
          />
        </Reveal>
        <Reveal index={3} style={{ gridArea: 'checked', display: 'flex' }}>
          <StatCard label="Checked in" value={data?.totalCheckedIn ?? 0} icon="✅" accent="#1a6b3c" drillLabel="Check-in" onClick={() => navigate('/manage-event')} />
        </Reveal>
        <Reveal index={4} style={{ gridArea: 'revenue', display: 'flex' }}>
          <StatCard label="Revenue" value={data?.totalRevenue ?? 0} icon="💳" accent="#f59e0b" format={(n) => money(n)} drillLabel="Tickets" onClick={() => navigate('/manage-event')} />
        </Reveal>
      </div>

      {/* ── GIVING pillar band ─────────────────────────────────────────────
          Shown only for tenants with the 'giving' module enabled. A separate
          band from the events bento above — giving dollars are never summed
          with event ticket revenue. */}
      {tenantModules.includes('giving') && (
        <div style={styles.givingBand}>
          <div style={styles.givingBandHead}>
            <h2 style={styles.givingBandTitle}>💚 Giving</h2>
            <button style={styles.givingBandLink} onClick={() => navigate('/giving')}>
              Campaigns →
            </button>
          </div>
          {giving && giving.campaignCount > 0 ? (
            <div style={styles.givingGrid}>
              <StatCard
                label="Raised"
                value={giving.totalRaised}
                icon="💚"
                accent="#1a6b3c"
                format={(n) => money(n)}
                hint={`${giving.percentToGoal}% of ${money(giving.combinedGoal)} goal`}
                drillLabel="Campaigns"
                onClick={() => navigate('/giving')}
              />
              <StatCard
                label="Active campaigns"
                value={giving.campaignCount}
                icon="📣"
                accent="#3ddc84"
                drillLabel="Campaigns"
                onClick={() => navigate('/giving')}
              />
              <StatCard
                label="Donors"
                value={giving.donorCount}
                icon="🧑‍🤝‍🧑"
                accent="#0ea5e9"
                drillLabel="Campaigns"
                onClick={() => navigate('/giving')}
              />
              <StatCard
                label="Still to raise"
                value={Math.max(giving.combinedGoal - giving.totalRaised, 0)}
                icon="⏳"
                accent="#c9a84c"
                format={(n) => money(n)}
                drillLabel="Campaigns"
                onClick={() => navigate('/giving')}
              />
            </div>
          ) : (
            <button style={styles.givingEmpty} onClick={() => navigate('/giving?new=1')}>
              <span style={{ fontSize: '1.4rem' }}>💚</span>
              <span>
                <span style={{ display: 'block', fontWeight: 800, color: '#0f2d1f' }}>No campaigns yet</span>
                <span style={{ fontSize: '0.8rem', color: '#5b7366' }}>Create your first Giving campaign →</span>
              </span>
            </button>
          )}
        </div>
      )}

      <h2 style={styles.sectionTitle}>Active Events</h2>
      {active.length === 0 ? (
        <div style={styles.emptyCard}>
          <p style={{ margin: 0, fontWeight: 600, color: '#0f2d1f' }}>No active events.</p>
          <p style={styles.muted}>Use the “Active event” picker in the sidebar to create one.</p>
        </div>
      ) : (
        <div style={styles.eventGrid}>{active.map(renderCard)}</div>
      )}

      {archived.length > 0 && (
        <>
          <h2 style={{ ...styles.sectionTitle, marginTop: '2rem' }}>Archived</h2>
          <div style={styles.eventGrid}>{archived.map(renderCard)}</div>
        </>
      )}

      {ctxEvents.length > 0 && (
        <p style={styles.hint}>
          Tip: the sidebar “Active event” picker switches which event is in scope. When an event
          is selected, this dashboard shows that event’s live operations.
        </p>
      )}
    </div>
  );
}

function StatusPill({ status, isPast }: { status: string; isPast: boolean }) {
  if (status === 'Archived') return <span style={styles.pillArchived}>Archived</span>;
  if (status === 'Draft') return <span style={styles.pillDraft}>Draft</span>;
  return <span style={isPast ? styles.pillPast : styles.pillUpcoming}>{isPast ? 'Past' : 'Upcoming'}</span>;
}

// ── Event mode (an event in scope) ──────────────────────────────────────────

interface OnboardingItem { key: string; label: string; done: boolean; detail: string; path: string; }
interface Onboarding { completed: number; total: number; percent: number; items: OnboardingItem[]; }

function EventDashboard() {
  const navigate = useNavigate();
  const { selectEvent, selectedEvent, selectedEventId, enabledModules } = useEvents();
  const isTicketed = enabledModules.includes('ticketing');
  const [data, setData] = useState<EventSummary | null>(null);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [rsvpRefresh, setRsvpRefresh] = useState(0);

  // Event briefing (option B): a quiet cheat sheet shown once per event per
  // session as a full banner, then collapsed to a re-summonable pill. We seed
  // the open/collapsed state from sessionStorage keyed by the event so that
  // arriving at a freshly-scoped event opens the briefing, while re-visiting an
  // event you've already been briefed on this session shows just the pill.
  const briefKey = selectedEventId ? `ep-briefed-${selectedEventId}` : null;
  const [briefOpen, setBriefOpen] = useState<boolean>(() => {
    if (!briefKey || typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(briefKey) !== 'collapsed';
  });

  function dismissBrief() {
    setBriefOpen(false);
    if (briefKey) try { window.sessionStorage.setItem(briefKey, 'collapsed'); } catch { /* ignore */ }
  }
  function reopenBrief() {
    setBriefOpen(true);
    if (briefKey) try { window.sessionStorage.removeItem(briefKey); } catch { /* ignore */ }
  }

  async function reloadSummary() {
    try {
      const res = await apiClient<EventSummary>('/dashboard/event');
      setData(res);
    } catch { /* keep prior */ }
  }

  async function handleExportReport() {
    if (!selectedEventId) return;
    setBusy('export');
    try {
      const res = await fetch(`${API_BASE}/dashboard/event/report.csv`, {
        headers: {
          Authorization: `Bearer ${getCurrentToken() ?? ''}`,
          'X-Event-Id': selectedEventId,
          ...(getTenantSlug() ? { 'X-Tenant-Slug': getTenantSlug() as string } : {}),
        },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-report-${(data?.eventName || 'event').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'Could not export the report.');
    } finally {
      setBusy('');
    }
  }

  async function handleGenerateGuests() {
    if (!selectedEventId) return;
    const input = window.prompt('How many demo guests to generate? (1–2000)', '250');
    if (input == null) return;
    const count = parseInt(input, 10);
    if (!Number.isFinite(count) || count < 1) { setError('Enter a number between 1 and 2000.'); return; }
    setBusy('generate');
    try {
      const res = await apiClient<{ created: number; total: number }>(
        `/events/${selectedEventId}/generate-guests`,
        { method: 'POST', body: { count } },
      );
      await reloadSummary();
      window.alert(`Generated ${res.created} demo guests. This event now has ${res.total} guests.`);
    } catch (e: any) {
      setError(e?.message || 'Could not generate guests.');
    } finally {
      setBusy('');
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await apiClient<EventSummary>('/dashboard/event');
        if (!cancelled) { setData(res); setError(''); }
        if (selectedEventId) {
          try {
            const ob = await apiClient<Onboarding>(`/events/${selectedEventId}/onboarding`);
            if (!cancelled) setOnboarding(ob);
          } catch { /* non-critical */ }
        }
      } catch {
        if (!cancelled) setError('Could not load this event’s summary.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const title = data?.eventName || selectedEvent?.name || 'Event';
  const checkInPct = data && data.guestCount > 0
    ? Math.round((data.checkedIn / data.guestCount) * 100)
    : 0;
  const seatedPct = data && data.seatCount > 0
    ? Math.round((data.assignedSeats / data.seatCount) * 100)
    : 0;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <button style={styles.backBtn} onClick={() => { selectEvent(null); navigate('/dashboard'); }}>
            ← All events
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <h1 style={styles.title}>{title}</h1>
            <span style={isTicketed ? styles.badgeTicketed : styles.badgeFree}>
              {isTicketed ? '🎟️ Ticketed' : '🎉 Free event'}
            </span>
          </div>
          <p style={styles.subtitle}>
            {formatDate(data?.date ?? selectedEvent?.date ?? null)} · 📍 {data?.venueName || selectedEvent?.venue?.name || 'TBD'}
          </p>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {loading && <p style={styles.muted}>Loading event summary…</p>}

      {/* Event briefing — a quiet "cheat sheet" snapshot assembled client-side
          from the summary + onboarding; shows once per event per session. */}
      {!loading && data && (
        <EventBriefing
          eventName={title}
          isTicketed={isTicketed}
          date={data.date ?? selectedEvent?.date ?? null}
          summary={data}
          onboarding={onboarding}
          open={briefOpen}
          onDismiss={dismissBrief}
          onReopen={reopenBrief}
          onNavigate={(p) => navigate(p)}
        />
      )}

      {/* Event Journey Summary — dashboard-style at-a-glance panel (header band,
          event overview with a days-until pill, and a guest-experience journey
          with a capacity donut, key metrics, and a five-stage journey strip). */}
      {!loading && data && (
        <EventJourneySummary
          eventName={title}
          tenantName={data.tenantName}
          isTicketed={isTicketed}
          date={data.date ?? selectedEvent?.date ?? null}
          startTime={data.startTime}
          endTime={data.endTime}
          venueName={data.venueName ?? selectedEvent?.venue?.name ?? null}
          maxCapacity={data.maxCapacity ?? selectedEvent?.maxCapacity ?? null}
          summary={data}
          onboarding={onboarding}
        />
      )}

      {/* Readiness Advisor — point-in-time gap detection, recommended next action */}
      {onboarding && <EventReadinessAdvisor data={onboarding} onNavigate={(p) => navigate(p)} />}

      {onboarding && (
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Onboarding Checklist
            </h2>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: onboarding.percent === 100 ? '#1a6b3c' : '#c9a84c' }}>
              {onboarding.completed}/{onboarding.total} · {onboarding.percent}%
            </span>
          </div>
          <div style={styles.obBar}>
            <div style={{ ...styles.obFill, width: `${onboarding.percent}%`, backgroundColor: onboarding.percent === 100 ? '#1a6b3c' : '#c9a84c' }} />
          </div>
          <div style={styles.obList}>
            {onboarding.items.map((it) => (
              <button
                key={it.key}
                style={styles.obItem}
                onClick={() => navigate(it.path)}
                title={`Go to ${it.label}`}
              >
                <span style={{ ...styles.obCheck, ...(it.done ? styles.obCheckDone : {}) }}>
                  {it.done ? '✓' : ''}
                </span>
                <span style={styles.obItemText}>
                  <span style={{ ...styles.obLabel, ...(it.done ? { color: '#6b7280', textDecoration: 'line-through' } : {}) }}>
                    {it.label}
                  </span>
                  <span style={styles.obDetail}>{it.detail}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Non-ticketed events: RSVP import instead of ticket orders. */}
      {!isTicketed && selectedEventId && (
        <div style={styles.section}>
          <RsvpImportPanel
            eventId={selectedEventId}
            onImported={() => {
              reloadSummary();
              setRsvpRefresh((n) => n + 1);
            }}
            onReset={() => {
              reloadSummary();
              setRsvpRefresh((n) => n + 1);
            }}
          />
          <RsvpImportHistory
            eventId={selectedEventId}
            refreshTrigger={rsvpRefresh}
            onReversed={() => {
              reloadSummary();
              setRsvpRefresh((n) => n + 1);
            }}
          />
        </div>
      )}

      {data && (
        <>
          <div style={styles.metricsGrid}>
            <Metric label="Guests" value={data.guestCount} accent clickable onClick={() => navigate('/manage-event')} />
            <Metric label="Zones" value={data.zoneCount} clickable onClick={() => navigate('/manage-event')} />
            <Metric label="Tables" value={data.tableCount} clickable onClick={() => navigate('/manage-event')} />
            <Metric label="Seats" value={data.seatCount} />
            <Metric label={`Seated (${seatedPct}%)`} value={data.assignedSeats} />
            <Metric label={`Checked In (${checkInPct}%)`} value={data.checkedIn} />
            <Metric label="Tickets Sold" value={data.ticketsSold} clickable onClick={() => navigate('/manage-event')} />
            <Metric label="Ticket Revenue" value={money(data.ticketRevenue)} />
          </div>

          {/* Progress bars for seating + check-in */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Readiness</h2>
            <ProgressRow label="Seats assigned" pct={seatedPct} count={`${data.assignedSeats} / ${data.seatCount}`} />
            <ProgressRow label="Guests checked in" pct={checkInPct} count={`${data.checkedIn} / ${data.guestCount}`} />
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Quick Actions</h2>
            <div style={styles.quickActions}>
              <button style={styles.btn} onClick={() => navigate('/manage-event')}>Seating &amp; Tables</button>
              <button style={styles.btnGold} onClick={() => navigate('/registrations')}>Guests &amp; Registrations</button>
              <button style={styles.btnGold} onClick={() => navigate('/events')}>Edit Event Details</button>
              <button style={styles.btn} disabled={busy === 'export'} onClick={handleExportReport}>
                {busy === 'export' ? 'Exporting…' : '⬇ Export Report (CSV)'}
              </button>
            </div>
          </div>

          {/* Demo tools — generate synthetic guests to drive seating/AI simulation */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Demo Tools</h2>
            <p style={{ ...styles.muted, marginTop: 0 }}>
              Populate this event with realistic synthetic guests (names, chapters, dietary
              needs, VIPs, and family groups) so you can run the seating engine and AI simulation.
            </p>
            <div style={styles.quickActions}>
              <button style={styles.btnGold} disabled={busy === 'generate'} onClick={handleGenerateGuests}>
                {busy === 'generate' ? 'Generating…' : '✨ Generate Demo Guests'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Small presentational helpers ────────────────────────────────────────────

function Metric({ label, value, accent, clickable, onClick }: {
  label: string; value: number | string; accent?: boolean; clickable?: boolean; onClick?: () => void;
}) {
  return (
    <div
      style={{ ...styles.metricCard, ...(clickable ? { cursor: 'pointer' } : {}) }}
      onClick={onClick}
    >
      <div style={{ ...styles.metricValue, ...(accent ? { color: '#c9a84c' } : {}) }}>{value}</div>
      <div style={styles.metricLabel}>{label}</div>
    </div>
  );
}

function ProgressRow({ label, pct, count }: { label: string; pct: number; count: string }) {
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <div style={styles.progressHeader}>
        <span>{label}</span>
        <span style={{ color: '#666' }}>{count} · {pct}%</span>
      </div>
      <div style={styles.progressBar}>
        <div style={{ ...styles.progressFill, width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },

  // Organization overview — green world
  orgHero: { position: 'relative', overflow: 'hidden', borderRadius: 20, padding: '26px 26px', marginBottom: 24, background: 'linear-gradient(120deg, #0f2d1f 0%, #14532d 55%, #1a6b3c 100%)', color: '#fff' },
  orgHeroGlow: { position: 'absolute', top: -80, right: -40, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.4), transparent 70%)', filter: 'blur(10px)', pointerEvents: 'none', transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1)' },
  orgKicker: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#86efac' },
  createEventBtn: { flexShrink: 0, background: '#c9a84c', color: '#0f2d1f', border: 'none', borderRadius: 8, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', whiteSpace: 'nowrap' },
  orgHeroTitle: { fontSize: '2rem', fontWeight: 800, margin: '8px 0 6px', letterSpacing: '-0.02em' },
  orgHeroSub: { fontSize: '0.95rem', color: '#bbf7d0', margin: 0, maxWidth: 560, lineHeight: 1.5 },

  orgBento: { display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gridTemplateRows: 'auto auto', gridTemplateAreas: '"hero guests seated" "hero checked revenue"', gap: 16, marginBottom: 28 },
  bentoTile: { textAlign: 'left', border: 'none', cursor: 'pointer', width: '100%' },
  orgBentoHero: { gridArea: 'hero', position: 'relative', overflow: 'hidden', borderRadius: 18, padding: '22px', background: 'linear-gradient(135deg, #1a6b3c, #15803d)', color: '#fff', minHeight: 180, boxShadow: '0 10px 30px -14px rgba(26,107,60,0.6)' },
  orgBentoHeroGlow: { position: 'absolute', bottom: -50, right: -30, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.22), transparent 70%)', pointerEvents: 'none' },
  orgBentoTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 },
  orgBentoChip: { fontSize: '0.82rem', fontWeight: 700, color: '#dcfce7' },
  orgBentoCta: { fontSize: '0.76rem', fontWeight: 700, color: '#bbf7d0' },
  orgBentoValue: { fontSize: '3.4rem', fontWeight: 800, lineHeight: 1, marginTop: 14, letterSpacing: '-0.03em', position: 'relative', zIndex: 1, fontVariantNumeric: 'tabular-nums' },
  orgBentoSplit: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, position: 'relative', zIndex: 1 },
  orgBentoSplitItem: { fontSize: '0.8rem', fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.14)', borderRadius: 999, padding: '3px 10px' },
  orgBentoSplitDot: { color: '#bbf7d0', fontSize: '0.8rem' },
  orgBentoSub: { fontSize: '0.82rem', color: '#dcfce7', marginTop: 10, position: 'relative', zIndex: 1 },

  advisorCard: { background: 'linear-gradient(135deg, #ffffff, #f3faf5)', border: '1px solid #d7e8dd', borderRadius: 16, padding: 18, marginBottom: 28, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  advisorHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  advisorTitle: { fontSize: '1.05rem', fontWeight: 800, color: '#0f2d1f', margin: 0 },
  advisorCount: { fontSize: '0.8rem', color: '#5b7366', fontWeight: 600 },
  advisorList: { display: 'flex', flexDirection: 'column', gap: 6 },
  advisorRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 12, border: '1px solid #e5efe9' },
  advisorName: { background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', padding: 0, display: 'flex', flexDirection: 'column', minWidth: 170, flex: 1 },
  advisorBar: { display: 'flex', alignItems: 'center', gap: 8, width: 150 },
  advisorNext: { background: '#eef7f1', color: '#1a6b3c', border: 'none', borderRadius: 999, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0.25rem 0 0.25rem 0' },
  subtitle: { color: '#666', fontSize: '0.9rem', margin: 0 },
  badgeTicketed: { background: '#eef2ff', color: '#4f46e5', borderRadius: 999, padding: '3px 12px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' },
  badgeFree: { background: '#ecfdf5', color: '#166534', borderRadius: 999, padding: '3px 12px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' },
  muted: { color: '#888', fontSize: '0.9rem' },
  hint: { color: '#888', fontSize: '0.8rem', marginTop: '1.5rem', fontStyle: 'italic' },
  backBtn: {
    background: 'none', border: 'none', color: '#1a6b3c', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.25rem',
  },
  metricsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem', marginBottom: '2rem',
  },
  metricCard: {
    padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0',
    borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  metricValue: { fontSize: '1.75rem', fontWeight: 700, color: '#1a6b3c', marginBottom: '0.25rem' },
  metricLabel: { fontSize: '0.78rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' },
  sectionTitle: {
    color: '#0f2d1f', fontSize: '1.15rem', marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem',
  },
  // ── Giving pillar band ───────────────────────────────────────────────────
  givingBand: {
    marginBottom: '1.75rem', padding: '16px 18px', borderRadius: 18,
    border: '1px solid #cfe6d8',
    background: 'linear-gradient(135deg, #ffffff 0%, #f3faf5 100%)',
  },
  givingBandHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  givingBandTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f2d1f', letterSpacing: '-0.01em' },
  givingBandLink: { background: 'none', border: 'none', cursor: 'pointer', color: '#1a6b3c', fontWeight: 700, fontSize: '0.82rem', padding: '4px 6px' },
  givingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 },
  givingEmpty: {
    display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
    padding: '18px', borderRadius: 14, border: '1px dashed #a7d4bb',
    background: 'rgba(26,107,60,0.04)', cursor: 'pointer',
  },
  section: {
    marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff',
    border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  eventGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem',
  },
  eventCard: {
    padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0',
    borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },
  eventCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  eventName: { fontSize: '1.1rem', fontWeight: 700, color: '#0f2d1f' },
  pillUpcoming: {
    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    color: '#1a6b3c', backgroundColor: '#e8f5e9', padding: '2px 8px', borderRadius: '10px',
  },
  pillPast: {
    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    color: '#777', backgroundColor: '#eee', padding: '2px 8px', borderRadius: '10px',
  },
  pillDraft: {
    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    color: '#8a6d00', backgroundColor: '#fdf3d4', padding: '2px 8px', borderRadius: '10px',
  },
  pillArchived: {
    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    color: '#555', backgroundColor: '#e0e0e0', padding: '2px 8px', borderRadius: '10px',
  },
  obBar: { width: '100%', height: '10px', backgroundColor: '#eee', borderRadius: '5px', overflow: 'hidden', margin: '0.75rem 0 1rem' },
  obFill: { height: '100%', borderRadius: '5px', transition: 'width 0.3s' },
  obList: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.5rem' },
  obItem: {
    display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.75rem',
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
    cursor: 'pointer', textAlign: 'left', width: '100%',
  },
  obCheck: {
    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
    border: '2px solid #d1d5db', color: '#fff', fontSize: '12px', fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  obCheckDone: { backgroundColor: '#1a6b3c', borderColor: '#1a6b3c' },
  obItemText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  obLabel: { fontSize: '0.85rem', fontWeight: 600, color: '#0f2d1f' },
  obDetail: { fontSize: '0.72rem', color: '#6b7280' },
  eventCardArchived: { opacity: 0.72 },
  cardActions: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' },
  linkBtn: {
    background: 'none', border: 'none', color: '#6b7280', fontSize: '0.78rem',
    fontWeight: 600, cursor: 'pointer', padding: '0.3rem 0.2rem',
  },
  eventMeta: { fontSize: '0.82rem', color: '#555' },
  eventStats: {
    display: 'flex', gap: '0.9rem', fontSize: '0.82rem', color: '#444',
    padding: '0.5rem 0', borderTop: '1px solid #f0f0f0', marginTop: '0.25rem',
  },
  workBtn: {
    marginTop: '0.25rem', padding: '0.55rem', backgroundColor: '#1a6b3c', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  },
  emptyCard: {
    padding: '2rem', backgroundColor: '#fff', border: '1px dashed #c9a84c',
    borderRadius: '10px', textAlign: 'center',
  },
  quickActions: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem' },
  btn: {
    padding: '0.55rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  },
  btnGold: {
    padding: '0.55rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  },
  progressHeader: { display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.35rem', color: '#333' },
  progressBar: { width: '100%', height: '14px', backgroundColor: '#eee', borderRadius: '7px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '7px', backgroundColor: '#1a6b3c', transition: 'width 0.3s' },
  error: {
    color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.6rem 0.9rem',
    borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem',
  },
};
